import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { usePlan } from '@/hooks/usePlan'
import { Icon } from '@/components/Icon'
import { Modal, ModalType } from '@/components/Modal'

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

interface TeamMember {
  id: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'member'
  is_active: boolean
  created_at: string
  last_login_at?: string
}

interface TeamLimits {
  currentCount: number
  maxMembers: number
  canAddMore: boolean
}

export const TeamMembers: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { getPlanLimit } = usePlan()
  const maxMembers = getPlanLimit('maxTeamMembers')

  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  const showModal = (title: string, message: string, type: ModalType = 'info', onConfirm?: () => void, confirmText?: string) => {
    setModal({ isOpen: true, title, message, type, onConfirm, confirmText })
  }

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  const [showAddModal, setShowAddModal] = useState(false)
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'member' as const })
  const [tempPassword, setTempPassword] = useState('')
  const [error, setError] = useState('')

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const response = await api.get('/team')
      return response.data.data as TeamMember[]
    },
  })

  const { data: limits, isLoading: limitsLoading } = useQuery({
    queryKey: ['team-limits'],
    queryFn: async () => {
      const response = await api.get('/team/limits')
      return response.data.data as TeamLimits
    },
  })

  const addMutation = useMutation({
    mutationFn: async (data: { name: string; email: string; role: string }) => {
      const response = await api.post('/team', data)
      return response.data.data
    },
    onSuccess: (data) => {
      setTempPassword(data.tempPassword || '')
      setNewMember({ name: '', email: '', role: 'member' })
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      queryClient.invalidateQueries({ queryKey: ['team-limits'] })
      setError('')
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to add team member')
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/team/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      queryClient.invalidateQueries({ queryKey: ['team-limits'] })
    },
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMember.name || !newMember.email) return
    addMutation.mutate(newMember)
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-500/20 text-purple-400 ring-purple-500/20'
      case 'admin':
        return 'bg-blue-500/20 text-blue-400 ring-blue-500/20'
      default:
        return 'bg-gray-500/20 text-gray-400 ring-gray-500/20'
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('team.roleOwner')
      case 'admin':
        return t('team.roleAdmin')
      default:
        return t('team.roleMember')
    }
  }

  const isLoading = membersLoading || limitsLoading
  const currentCount = limits?.currentCount || members?.length || 0
  const canAddMore = limits?.canAddMore ?? (maxMembers === -1 || currentCount < maxMembers)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
        isLoading={removeMutation.isPending}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('team.title')}</h1>
          <p className="text-gray-400 mt-1">
            {maxMembers === -1
              ? t('team.unlimitedMembers', { count: currentCount })
              : t('team.membersCount', { current: currentCount, max: maxMembers })}
          </p>
        </div>
        {canAddMore && (
          <button
            onClick={() => {
              setShowAddModal(true)
              setTempPassword('')
              setError('')
            }}
            className="px-4 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#006cd4]/80 transition-colors flex items-center gap-2"
          >
            <Icon name="plus" className="w-4" />
            {t('team.addMember')}
          </button>
        )}
      </div>

      {/* Members List */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">{t('team.name')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">{t('team.email')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">{t('team.role')}</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">{t('team.joined')}</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">{t('team.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {members?.map((member) => (
              <tr key={member.id} className="border-b border-white/5 last:border-0">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4] text-sm font-medium">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium">{member.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">{member.email}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getRoleBadgeColor(member.role)}`}>
                    {getRoleLabel(member.role)}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(member.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  {member.role !== 'owner' && (
                    <button
                      onClick={() => {
                        showModal(
                          t('team.removeTitle') || 'Remove Team Member',
                          t('team.confirmRemove'),
                          'confirm',
                          () => removeMutation.mutate(member.id),
                          t('team.remove')
                        )
                      }}
                      className="text-gray-400 hover:text-red-400 transition-colors"
                      title={t('team.remove')}
                    >
                      <Icon name="trash" className="w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(!members || members.length === 0) && (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  {t('team.noMembers')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            {!tempPassword ? (
              <>
                <h2 className="text-xl font-bold mb-4">{t('team.addMember')}</h2>
                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
                <form onSubmit={handleAdd} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">{t('team.name')}</label>
                    <input
                      type="text"
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">{t('team.email')}</label>
                    <input
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">{t('team.role')}</label>
                    <select
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value as 'member' })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
                    >
                      <option value="member">{t('team.roleMember')}</option>
                      <option value="admin">{t('team.roleAdmin')}</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={addMutation.isPending}
                      className="flex-1 px-4 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {addMutation.isPending ? t('common.loading') : t('team.addMember')}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 mx-auto mb-3">
                    <Icon name="check" className="w-6" />
                  </div>
                  <h2 className="text-xl font-bold">{t('team.memberAdded')}</h2>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-400 mb-2">{t('team.tempPassword')}</p>
                  <code className="block bg-black/30 rounded px-3 py-2 text-sm font-mono text-white break-all">
                    {tempPassword}
                  </code>
                  <p className="text-xs text-yellow-400/70 mt-2">{t('team.sharePassword')}</p>
                </div>
                <button
                  onClick={() => {
                    setShowAddModal(false)
                    setTempPassword('')
                  }}
                  className="w-full px-4 py-2 bg-[#006cd4] hover:bg-[#006cd4]/80 rounded-lg transition-colors"
                >
                  {t('common.done')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
